"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import { useMatch } from "fansunited-data-layer/client";
import { calculateH2HStats } from "fansunited-data-layer";
import { StatsHeadToHead } from "fansunited-sports-ui";
import { MatchList } from "@/components/matches";
import type { FUSportsMatchWithNativeId } from "@/utilities/data/types";
import { mapMatchToMatchListItem } from "@/utilities/mappers/match";
import { useTeamLogoUrl } from "@/components/ui/TeamLogo";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
interface EmbeddedH2HProps {
  homeTeamId: string;
  awayTeamId: string;
  homeTeamColor?: string;
  awayTeamColor?: string;
}
export function EmbeddedH2H(t0) {
  const $ = _c(73);
  const {
    homeTeamId,
    awayTeamId,
    homeTeamColor,
    awayTeamColor
  } = t0;
  const labels = useLabels();
  const nav = useNavigation();
  const resolveLogoUrl = useTeamLogoUrl();
  const {
    headToHeadMatches: t1
  } = useMatch();
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const headToHeadMatches = t2;
  let t3;
  if ($[2] !== awayTeamId || $[3] !== headToHeadMatches || $[4] !== homeTeamId) {
    t3 = calculateH2HStats(headToHeadMatches, homeTeamId, awayTeamId);
    $[2] = awayTeamId;
    $[3] = headToHeadMatches;
    $[4] = homeTeamId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const h2hStats = t3;
  const h2hLabels = labels.matchPage.h2hTab;
  let t4;
  if ($[6] !== headToHeadMatches) {
    t4 = [...headToHeadMatches].sort(_temp);
    $[6] = headToHeadMatches;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const sortedMatches = t4;
  let t5;
  if ($[8] !== nav || $[9] !== resolveLogoUrl || $[10] !== sortedMatches) {
    let t6;
    if ($[12] !== nav || $[13] !== resolveLogoUrl) {
      t6 = match => mapMatchToMatchListItem(match as FUSportsMatchWithNativeId, true, true, nav.match, resolveLogoUrl);
      $[12] = nav;
      $[13] = resolveLogoUrl;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    t5 = sortedMatches.map(t6);
    $[8] = nav;
    $[9] = resolveLogoUrl;
    $[10] = sortedMatches;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const items = t5;
  const leftColor = homeTeamColor || "#333333";
  const rightColor = awayTeamColor || "#0284c7";
  let t6;
  if ($[15] !== leftColor || $[16] !== rightColor) {
    t6 = {
      "--h2h-left-color": leftColor,
      "--h2h-right-color": rightColor
    };
    $[15] = leftColor;
    $[16] = rightColor;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const cssVars = t6 as React.CSSProperties;
  let t7;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      padding: "md" as const,
      background: "bg-card",
      borderRadius: "lg" as const,
      itemSpacing: "md" as const,
      bar: {
        leftBarColor: "bg-[var(--h2h-left-color)]",
        rightBarColor: "bg-[var(--h2h-right-color)]",
        height: "md" as const,
        borderRadius: "full" as const
      },
      typography: {
        label: "text-sm font-medium text-muted-foreground text-center",
        leftValue: "text-sm font-bold",
        rightValue: "text-sm font-bold"
      }
    };
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const styles = t7;
  if (h2hStats.totalMatches === 0) {
    let t8;
    if ($[19] !== h2hLabels.noH2HMatches) {
      t8 = <div className="text-center py-6 text-muted-foreground text-sm">{h2hLabels.noH2HMatches}</div>;
      $[19] = h2hLabels.noH2HMatches;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    return t8;
  }
  const t8 = h2hLabels.wins;
  const t9 = h2hStats.homeTeamWins;
  const t10 = h2hStats.awayTeamWins;
  const t11 = h2hStats.homeTeamWins;
  let t12;
  if ($[21] !== h2hStats.homeTeamWinPercentage) {
    t12 = h2hStats.homeTeamWinPercentage.toFixed(0);
    $[21] = h2hStats.homeTeamWinPercentage;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = `${t11} (${t12}%)`;
  const t14 = h2hStats.awayTeamWins;
  let t15;
  if ($[23] !== h2hStats.awayTeamWinPercentage) {
    t15 = h2hStats.awayTeamWinPercentage.toFixed(0);
    $[23] = h2hStats.awayTeamWinPercentage;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const t16 = `${t14} (${t15}%)`;
  let t17;
  if ($[25] !== h2hLabels.wins || $[26] !== h2hStats.awayTeamWins || $[27] !== h2hStats.homeTeamWins || $[28] !== t13 || $[29] !== t16) {
    t17 = {
      id: "wins",
      label: t8,
      leftValue: t9,
      rightValue: t10,
      leftDisplay: t13,
      rightDisplay: t16,
      variant: "comparison"
    };
    $[25] = h2hLabels.wins;
    $[26] = h2hStats.awayTeamWins;
    $[27] = h2hStats.homeTeamWins;
    $[28] = t13;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  const t18 = h2hLabels.goalsScored;
  const t19 = h2hStats.homeTeamGoals;
  const t20 = h2hStats.awayTeamGoals;
  const t21 = String(h2hStats.homeTeamGoals);
  const t22 = String(h2hStats.awayTeamGoals);
  let t23;
  if ($[31] !== h2hLabels.goalsScored || $[32] !== h2hStats.awayTeamGoals || $[33] !== h2hStats.homeTeamGoals || $[34] !== t21 || $[35] !== t22) {
    t23 = {
      id: "goals",
      label: t18,
      leftValue: t19,
      rightValue: t20,
      leftDisplay: t21,
      rightDisplay: t22,
      variant: "comparison"
    };
    $[31] = h2hLabels.goalsScored;
    $[32] = h2hStats.awayTeamGoals;
    $[33] = h2hStats.homeTeamGoals;
    $[34] = t21;
    $[35] = t22;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  const t24 = h2hLabels.avgGoalsPerMatch;
  const t25 = h2hStats.homeTeamAvgGoals;
  const t26 = h2hStats.awayTeamAvgGoals;
  let t27;
  if ($[37] !== h2hStats.homeTeamAvgGoals) {
    t27 = h2hStats.homeTeamAvgGoals.toFixed(2);
    $[37] = h2hStats.homeTeamAvgGoals;
    $[38] = t27;
  } else {
    t27 = $[38];
  }
  let t28;
  if ($[39] !== h2hStats.awayTeamAvgGoals) {
    t28 = h2hStats.awayTeamAvgGoals.toFixed(2);
    $[39] = h2hStats.awayTeamAvgGoals;
    $[40] = t28;
  } else {
    t28 = $[40];
  }
  let t29;
  if ($[41] !== h2hLabels.avgGoalsPerMatch || $[42] !== h2hStats.awayTeamAvgGoals || $[43] !== h2hStats.homeTeamAvgGoals || $[44] !== t27 || $[45] !== t28) {
    t29 = {
      id: "avg-goals",
      label: t24,
      leftValue: t25,
      rightValue: t26,
      leftDisplay: t27,
      rightDisplay: t28,
      variant: "comparison"
    };
    $[41] = h2hLabels.avgGoalsPerMatch;
    $[42] = h2hStats.awayTeamAvgGoals;
    $[43] = h2hStats.homeTeamAvgGoals;
    $[44] = t27;
    $[45] = t28;
    $[46] = t29;
  } else {
    t29 = $[46];
  }
  let t30;
  if ($[47] !== t17 || $[48] !== t23 || $[49] !== t29) {
    t30 = <StatsHeadToHead items={[t17, t23, t29]} styles={styles} />;
    $[47] = t17;
    $[48] = t23;
    $[49] = t29;
    $[50] = t30;
  } else {
    t30 = $[50];
  }
  let t31;
  if ($[51] !== h2hStats.totalMatches) {
    t31 = <span className="font-medium">{h2hStats.totalMatches}</span>;
    $[51] = h2hStats.totalMatches;
    $[52] = t31;
  } else {
    t31 = $[52];
  }
  const t32 = h2hLabels.matchesPlayed;
  let t33;
  if ($[53] !== h2hStats.draws) {
    t33 = <span className="font-medium">{h2hStats.draws}</span>;
    $[53] = h2hStats.draws;
    $[54] = t33;
  } else {
    t33 = $[54];
  }
  const t34 = h2hLabels.draws;
  let t35;
  if ($[55] !== h2hStats.drawPercentage) {
    t35 = h2hStats.drawPercentage.toFixed(0);
    $[55] = h2hStats.drawPercentage;
    $[56] = t35;
  } else {
    t35 = $[56];
  }
  let t36;
  if ($[57] !== h2hLabels.draws || $[58] !== h2hLabels.matchesPlayed || $[59] !== t31 || $[60] !== t33 || $[61] !== t35) {
    t36 = <div className="px-4 pb-4 text-center text-sm text-muted-foreground">{t31} {t32} •{" "}{t33} {t34} ({t35}%)</div>;
    $[57] = h2hLabels.draws;
    $[58] = h2hLabels.matchesPlayed;
    $[59] = t31;
    $[60] = t33;
    $[61] = t35;
    $[62] = t36;
  } else {
    t36 = $[62];
  }
  let t37;
  if ($[63] !== cssVars || $[64] !== t30 || $[65] !== t36) {
    t37 = <div className="bg-card" style={cssVars}>{t30}{t36}</div>;
    $[63] = cssVars;
    $[64] = t30;
    $[65] = t36;
    $[66] = t37;
  } else {
    t37 = $[66];
  }
  let t38;
  if ($[67] !== items || $[68] !== labels.matches.noMatchesInRound) {
    t38 = <MatchList matches={items} variant="teamFixtures" emptyMessage={labels.matches.noMatchesInRound} />;
    $[67] = items;
    $[68] = labels.matches.noMatchesInRound;
    $[69] = t38;
  } else {
    t38 = $[69];
  }
  let t39;
  if ($[70] !== t37 || $[71] !== t38) {
    t39 = <div className="space-y-6">{t37}{t38}</div>;
    $[70] = t37;
    $[71] = t38;
    $[72] = t39;
  } else {
    t39 = $[72];
  }
  return t39;
}
function _temp(a, b) {
  return new Date(b.timing.kickoffTime).getTime() - new Date(a.timing.kickoffTime).getTime();
}